/**
 * CobradosPage
 * Bandeja de pagos cobrados (vista de consulta).
 *
 * Lee del endpoint real: GET /credit-worker/backoffice/collection/payments
 * — devuelve los CollectionRecord con status FULL/PARTIAL en el rango,
 *   con timestamp real (`paid_at`), método y desglose (capital/intereses/IVA).
 *
 * Hasta antes los pagos se sintetizaban desde `weeks_paid + disbursed_at`
 * (banner amarillo de "datos aproximados"). Esa lógica se removió cuando el
 * backend expuso `/collection/payments`.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Search, User } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { ROUTES, PARTNER_ID } from '@/constants';
import { useCollectionPayments } from '@/features/collection/hooks';
import { formatCurrency, formatNumber } from '@/utils/formatters';
import type { CollectionPayment } from '@/types/collection.types';

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Cobrados', path: ROUTES.COLLECTION_COBRADOS },
];

type Period = 'today' | 'week' | 'month' | 'all';
type SortKey = 'cliente' | 'credito' | 'monto' | 'fecha' | 'cupon';
type SortDir = 'asc' | 'desc';

// El backend exige un rango finito; "Todos" usa una ventana razonable.
const ALL_LOOKBACK_DAYS = 365;

// ─── Main ─────────────────────────────────────────────────────────────────

export function CobradosPage() {
  const [period, setPeriod] = useState<Period>('month');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('fecha');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const { from, to } = useMemo(() => buildRange(period), [period]);
  const { payments, summary, isLoading, refetch } = useCollectionPayments(PARTNER_ID, from, to);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = payments;
    if (q) {
      rows = rows.filter((r) => {
        const name = (r.full_name ?? '').toLowerCase();
        const phone = (r.phone_number ?? '').toLowerCase();
        const id = (r.credit_id ?? '').toLowerCase();
        return name.includes(q) || phone.includes(q) || id.includes(q);
      });
    }
    return [...rows].sort((a, b) => comparePayment(a, b, sortBy, sortDir));
  }, [payments, search, sortBy, sortDir]);

  // KPIs vienen del summary del backend; si todavía no hay datos, fallback a 0.
  const totalAmount = summary?.total_collected_mxn ?? 0;
  const totalCount = summary?.payments_count ?? 0;
  const avgAmount = summary?.avg_payment_mxn ?? 0;
  const uniqueClients = summary?.unique_clients ?? 0;

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Cobrados" subtitle="Pagos recibidos" breadcrumbs={BREADCRUMBS} />
        <Skeleton height={400} variant="rectangular" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Cobrados"
        subtitle="Pagos recibidos · histórico de cobranza"
        breadcrumbs={BREADCRUMBS}
        actions={
          <Button onClick={() => refetch()}>
            <RefreshCw size={16} />
            Actualizar
          </Button>
        }
      />

      <div className="space-y-4">
        {/* Filtros temporales + KPIs */}
        <div className="grid gap-3 lg:grid-cols-[auto_1fr]">
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card p-2">
            <PeriodTab label="Hoy" active={period === 'today'} onClick={() => setPeriod('today')} />
            <PeriodTab label="Semana" active={period === 'week'} onClick={() => setPeriod('week')} />
            <PeriodTab label="Mes" active={period === 'month'} onClick={() => setPeriod('month')} />
            <PeriodTab label="Todos" active={period === 'all'} onClick={() => setPeriod('all')} />
          </div>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Kpi label="Total cobrado" value={formatCurrency(totalAmount, 'MXN', 'es-MX')} accent="green" />
            <Kpi label="Pagos" value={formatNumber(totalCount)} accent="blue" />
            <Kpi label="Pago promedio" value={formatCurrency(avgAmount, 'MXN', 'es-MX')} accent="default" />
            <Kpi label="Clientes únicos" value={formatNumber(uniqueClients)} accent="default" />
          </div>
        </div>

        {/* Tabla de pagos */}
        <Card>
          <div className="mb-3 flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
            <Search size={14} className="text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar nombre, teléfono o ID de crédito..."
              className="flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground focus:outline-none"
            />
          </div>

          {visibleRows.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              No hay pagos en este período.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/30">
                  <tr>
                    <Th label="Cliente" sortKey="cliente" sortBy={sortBy} sortDir={sortDir} onSort={(k) => toggleSort(k, sortBy, sortDir, setSortBy, setSortDir)} />
                    <Th label="Crédito" sortKey="credito" sortBy={sortBy} sortDir={sortDir} onSort={(k) => toggleSort(k, sortBy, sortDir, setSortBy, setSortDir)} />
                    <Th label="Cupón" sortKey="cupon" sortBy={sortBy} sortDir={sortDir} onSort={(k) => toggleSort(k, sortBy, sortDir, setSortBy, setSortDir)} />
                    <Th label="Monto" sortKey="monto" sortBy={sortBy} sortDir={sortDir} onSort={(k) => toggleSort(k, sortBy, sortDir, setSortBy, setSortDir)} align="right" />
                    <Th label="Fecha de cobro" sortKey="fecha" sortBy={sortBy} sortDir={sortDir} onSort={(k) => toggleSort(k, sortBy, sortDir, setSortBy, setSortDir)} />
                    <th className="px-2.5 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Método
                    </th>
                    <th className="w-[36px]" />
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.slice(0, 100).map((p) => (
                    <PaymentTr key={p.payment_id} payment={p} />
                  ))}
                </tbody>
              </table>

              <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-[11px] text-muted-foreground">
                <span>Mostrando {Math.min(visibleRows.length, 100)} de {visibleRows.length} pagos</span>
                {visibleRows.length > 100 && <span className="text-muted-foreground/70">Use búsqueda o cambie de período para acotar</span>}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────

function PeriodTab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${
        active
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
      }`}
    >
      {label}
    </button>
  );
}

function Kpi({ label, value, accent }: { label: string; value: string; accent: 'default' | 'blue' | 'green' }) {
  const colorClass =
    accent === 'green' ? 'text-green-500' : accent === 'blue' ? 'text-blue-400' : 'text-foreground';
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`mt-1 text-lg font-extrabold tabular-nums ${colorClass}`}>{value}</p>
    </div>
  );
}

function Th({
  label, sortKey, sortBy, sortDir, onSort, align,
}: {
  label: string; sortKey: SortKey; sortBy: SortKey; sortDir: SortDir;
  onSort: (k: SortKey) => void; align?: 'left' | 'right';
}) {
  const isSorted = sortBy === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      className={`cursor-pointer select-none px-2.5 py-2 text-[10px] font-semibold uppercase tracking-wide transition-colors ${
        align === 'right' ? 'text-right' : 'text-left'
      } ${isSorted ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        <span className="text-[9px] opacity-70">{isSorted ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
      </span>
    </th>
  );
}

function PaymentTr({ payment }: { payment: CollectionPayment }) {
  const creditDetailLink = `/credit/detail/${payment.credit_id}?directoryId=${payment.directory_id}&from=cobrados`;
  const expedienteLink = `/backoffice/expedientes/${payment.directory_id}?from=cobrados`;
  return (
    <tr className="cursor-pointer border-b border-border/30 hover:bg-accent/30">
      <td className="px-2.5 py-2">
        <Link to={creditDetailLink} className="block">
          <div className="font-medium text-foreground">{payment.full_name || '—'}</div>
          <div className="text-[10px] text-muted-foreground">{payment.phone_number || '—'}</div>
        </Link>
      </td>
      <td className="px-2.5 py-2">
        <Link to={creditDetailLink} className="block">
          <span className="font-mono text-[10px] text-muted-foreground">{payment.credit_id.slice(0, 12)}</span>
        </Link>
      </td>
      <td className="px-2.5 py-2 text-foreground">
        <Link to={creditDetailLink} className="block">
          {payment.coupon_number}{payment.weeks_total ? ` / ${payment.weeks_total}` : ''}
        </Link>
      </td>
      <td className="px-2.5 py-2 text-right tabular-nums text-green-500">
        <Link to={creditDetailLink} className="block">
          {formatCurrency(payment.amount_mxn, 'MXN', 'es-MX')}
          {payment.is_partial && <span className="ml-1 text-[9px] font-semibold uppercase text-yellow-400">parcial</span>}
        </Link>
      </td>
      <td className="px-2.5 py-2 text-foreground">
        <Link to={creditDetailLink} className="block">
          {formatDateLong(payment.paid_at)}
        </Link>
      </td>
      <td className="px-2.5 py-2">
        <Link to={creditDetailLink} className="block">
          <span className="inline-block rounded bg-blue-900/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-400">
            {formatMethod(payment.method)}
          </span>
        </Link>
      </td>
      <td className="px-1 py-2 text-right">
        <Link
          to={expedienteLink}
          title="Ver expediente del cliente"
          aria-label="Ver expediente del cliente"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <User size={14} />
        </Link>
      </td>
    </tr>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function buildRange(period: Period): { from: string; to: string } {
  const today = todayISO();
  if (period === 'today') return { from: today, to: today };
  const lookback = period === 'week' ? 7 : period === 'month' ? 30 : ALL_LOOKBACK_DAYS;
  const fromDate = new Date();
  fromDate.setUTCDate(fromDate.getUTCDate() - lookback);
  return { from: toISODate(fromDate), to: today };
}

function todayISO(): string {
  return toISODate(new Date());
}

function toISODate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function comparePayment(a: CollectionPayment, b: CollectionPayment, sortBy: SortKey, dir: SortDir): number {
  const factor = dir === 'asc' ? 1 : -1;
  switch (sortBy) {
    case 'cliente':
      return (a.full_name ?? '').localeCompare(b.full_name ?? '') * factor;
    case 'credito':
      return a.credit_id.localeCompare(b.credit_id) * factor;
    case 'cupon':
      return (a.coupon_number - b.coupon_number) * factor;
    case 'monto':
      return (a.amount_mxn - b.amount_mxn) * factor;
    case 'fecha':
      return a.paid_at.localeCompare(b.paid_at) * factor;
  }
  return 0;
}

function toggleSort(
  key: SortKey,
  current: SortKey,
  dir: SortDir,
  setBy: (k: SortKey) => void,
  setDir: (d: SortDir) => void,
) {
  if (key === current) {
    setDir(dir === 'asc' ? 'desc' : 'asc');
  } else {
    setBy(key);
    setDir('desc');
  }
}

function formatDateLong(iso: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatMethod(m: CollectionPayment['method']): string {
  switch (m) {
    case 'WALLET_MXN': return 'Wallet MXN';
    case 'WALLET_USD': return 'Wallet USD';
    case 'SPEI': return 'SPEI';
    case 'STORE': return 'Tienda';
    case 'OTHER': return 'Otro';
    default: return m;
  }
}
